import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export interface BrandingSelection {
  organizationDisplayName?: string | null;
  logoUrl?: string | null;
  headerLogoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  tone?: string | null;
}

interface BrandingReviewCardProps {
  title: string;
  subtitle?: string;
  confidence?: number | null;
  warnings?: string[];
  selection: BrandingSelection;
  editable?: boolean;
  actions?: React.ReactNode;
  onChange?: (next: BrandingSelection) => void;
}

function colorValue(value?: string | null, fallback = '#e5e7eb') {
  return value && value.trim() ? value.trim() : fallback;
}

export default function BrandingReviewCard({
  title,
  subtitle,
  confidence,
  warnings = [],
  selection,
  editable = false,
  actions,
  onChange,
}: BrandingReviewCardProps) {
  const setField = (field: keyof BrandingSelection, value: string) => {
    if (!onChange) return;
    onChange({
      ...selection,
      [field]: value,
    });
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5}}>
          <Box>
            <Typography variant="h6" sx={{fontSize: 16, fontWeight: 600}}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {confidence !== undefined && confidence !== null && (
            <Chip
              size="small"
              color={confidence >= 0.65 ? 'success' : confidence >= 0.45 ? 'warning' : 'default'}
              label={`${Math.round(confidence * 100)}% confidence`}
            />
          )}
        </Box>

        <Box sx={{display: 'grid', gridTemplateColumns: '72px 72px 1fr auto', alignItems: 'center', gap: 2}}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            {selection.logoUrl ? (
              <img
                src={selection.logoUrl}
                alt="Brand logo"
                style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No brand logo
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            {selection.headerLogoUrl ? (
              <img
                src={selection.headerLogoUrl}
                alt="Header logo"
                style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No header logo
              </Typography>
            )}
          </Box>
          <Box sx={{minWidth: 0}}>
            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
              {selection.organizationDisplayName || 'Unnamed organization'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tone: {selection.tone || 'NEUTRAL'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Box
              title={`Primary ${selection.primaryColor || ''}`}
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: '1px solid #d1d5db',
                bgcolor: colorValue(selection.primaryColor),
              }}
            />
            <Box
              title={`Secondary ${selection.secondaryColor || ''}`}
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: '1px solid #d1d5db',
                bgcolor: colorValue(selection.secondaryColor),
              }}
            />
          </Stack>
        </Box>

        {warnings.length > 0 && (
          <Stack spacing={1}>
            {warnings.map((warning, index) => (
              <Alert key={`${warning}-${index}`} severity="warning" variant="outlined">
                {warning}
              </Alert>
            ))}
          </Stack>
        )}

        {editable && (
          <Stack spacing={1.5}>
            <TextField
              label="Organization display name"
              size="small"
              value={selection.organizationDisplayName || ''}
              onChange={event => setField('organizationDisplayName', event.target.value)}
            />
            <TextField
              label="Brand logo URL"
              size="small"
              value={selection.logoUrl || ''}
              onChange={event => setField('logoUrl', event.target.value)}
            />
            <TextField
              label="Header logo URL"
              size="small"
              value={selection.headerLogoUrl || ''}
              onChange={event => setField('headerLogoUrl', event.target.value)}
            />
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5}}>
              <TextField
                label="Primary color"
                size="small"
                value={selection.primaryColor || ''}
                onChange={event => setField('primaryColor', event.target.value)}
              />
              <TextField
                label="Secondary color"
                size="small"
                value={selection.secondaryColor || ''}
                onChange={event => setField('secondaryColor', event.target.value)}
              />
            </Box>
            <TextField
              select
              label="Tone"
              size="small"
              value={selection.tone || 'NEUTRAL'}
              onChange={event => setField('tone', event.target.value)}
            >
              <MenuItem value="FORMAL">Formal</MenuItem>
              <MenuItem value="FRIENDLY">Friendly</MenuItem>
              <MenuItem value="NEUTRAL">Neutral</MenuItem>
            </TextField>
          </Stack>
        )}

        {actions && <Box sx={{pt: 0.5}}>{actions}</Box>}
      </CardContent>
    </Card>
  );
}
